import { ReportTypeEnum } from '@revelio/data-access';

import { GLOBAL_ASSISTANT_REPORT_CATALOG } from './catalog-data';
import type {
  GlobalAssistantReportCatalogEntry,
  GlobalAssistantReportOfferCatalogEntry,
  GlobalAssistantReportParameterCatalogEntry,
  GlobalAssistantReportParameterDescriptor,
  GlobalAssistantReportParameterMapping,
} from './types';

export {
  GLOBAL_AI_ASSISTANT_REPORT_SELECTOR_CONFIDENCE_THRESHOLD,
  GLOBAL_ASSISTANT_REPORT_CATALOG,
} from './catalog-data';
export type {
  GlobalAssistantReportCatalogEntry,
  GlobalAssistantReportLaunchTarget,
  GlobalAssistantReportListFilterType,
  GlobalAssistantReportOfferCatalogEntry,
  GlobalAssistantReportParameterCatalogEntry,
  GlobalAssistantReportParameterDescriptor,
  GlobalAssistantReportParameterMapping,
  GlobalAssistantReportParameterResolverKind,
  GlobalAssistantReportSelectorHints,
  ReportProjectionRule,
  TdFilterField,
} from './types';

const buildParameterSummary = (
  entry: GlobalAssistantReportCatalogEntry
): string =>
  entry.parameters.map((parameter) => parameter.displayName).join(', ');

export const getEnabledCatalogEntries =
  (): readonly GlobalAssistantReportCatalogEntry[] =>
    GLOBAL_ASSISTANT_REPORT_CATALOG.filter((entry) => entry.enabled);

export const getEnabledReportTypes = (): ReportTypeEnum[] =>
  getEnabledCatalogEntries().map((entry) => entry.reportType);

export const getCatalogEntry = (
  reportType: ReportTypeEnum
): GlobalAssistantReportCatalogEntry | undefined =>
  GLOBAL_ASSISTANT_REPORT_CATALOG.find(
    (entry) => entry.reportType === reportType && entry.enabled
  );

export const getGlobalAssistantReportCatalogEntry = (
  reportType: ReportTypeEnum
): GlobalAssistantReportOfferCatalogEntry | undefined => {
  const entry = getCatalogEntry(reportType);
  if (!entry) {
    return undefined;
  }
  return {
    reportType: entry.reportType,
    reportName: entry.reportName,
    description: entry.description,
    sampleLink: entry.sampleLink,
    parameterSummary: buildParameterSummary(entry),
  };
};

export const GLOBAL_AI_ASSISTANT_V1_REPORT_CATALOG: readonly GlobalAssistantReportOfferCatalogEntry[] =
  getEnabledCatalogEntries().map((entry) => ({
    reportType: entry.reportType,
    reportName: entry.reportName,
    description: entry.description,
    sampleLink: entry.sampleLink,
    parameterSummary: buildParameterSummary(entry),
  }));

export const getReportParameterCatalog = (
  reportType: ReportTypeEnum
): GlobalAssistantReportParameterCatalogEntry | undefined => {
  const entry = getCatalogEntry(reportType);
  if (!entry) {
    return undefined;
  }
  return {
    reportType: entry.reportType,
    parameters: entry.parameters,
  };
};

export const getReportParameterDescriptor = (
  reportType: ReportTypeEnum,
  parameterId: string
): GlobalAssistantReportParameterDescriptor | undefined =>
  getReportParameterCatalog(reportType)?.parameters.find(
    (parameter) => parameter.parameterId === parameterId
  );

export const formatReportParametersForPrompt = (
  reportType: ReportTypeEnum
): string => {
  const catalog = getReportParameterCatalog(reportType);
  if (!catalog) {
    return '';
  }
  return catalog.parameters
    .map((parameter) => {
      const multiLabel = parameter.supportsMultiple ? ', multiValue: true' : '';
      const listLabel = parameter.listFilterType
        ? `, listFilter: ${parameter.listFilterType}`
        : '';
      return `- parameterId: "${parameter.parameterId}", displayName: "${parameter.displayName}", resolver: ${parameter.resolverKind}, categories: [${parameter.expectedSelectionCategories.join(', ')}]${listLabel}${multiLabel}`;
    })
    .join('\n');
};

export const buildReportSelectorCatalogLines = (): string =>
  getEnabledCatalogEntries()
    .map(
      (entry) =>
        `- ${entry.reportType}: ${entry.reportName} — ${entry.description}. Parameters: ${buildParameterSummary(entry)}`
    )
    .join('\n');

export const buildReportSelectorHintLines = (): string =>
  getEnabledCatalogEntries()
    .map((entry) => {
      const whenNotToPick = entry.selectorHints.whenNotToPick
        ? ` Do not pick when: ${entry.selectorHints.whenNotToPick}`
        : '';
      return `- ${entry.reportType}: ${entry.selectorHints.whenToPick}${whenNotToPick}`;
    })
    .join('\n');

// TEMPORARY: deterministic test prompts for manual QA. Remove once the model
// reliably maps natural language to each report without few-shot examples.
// Filter values are resolved separately via AI Search, not here.
const REPORT_SELECTOR_TEST_EXAMPLES: readonly {
  prompt: string;
  aliases?: readonly string[];
  reportType: ReportTypeEnum;
  reason: string;
}[] = [
  {
    prompt:
      'compare software engineer in San Francisco vs Austin for a job offer',
    reportType: ReportTypeEnum.JobComparison,
    reason: 'job-to-job comparison',
  },
  {
    prompt:
      'market conditions in ny vs london for senior engineers with electrician skills',
    reportType: ReportTypeEnum.MarketTightness,
    reason: 'labor market comparison',
  },
  {
    prompt: 'compare employee sentiment at Google and Microsoft',
    reportType: ReportTypeEnum.Sentiment,
    reason: 'company sentiment comparison',
  },
  {
    prompt: 'company prestige score for Amazon',
    aliases: ['prestige analysis for Amazon'],
    reportType: ReportTypeEnum.Prestige,
    reason: 'company prestige',
  },
];

export const resolveReportSelectorTestPrompt = (
  prompt: string
): ReportTypeEnum | null => {
  const normalized = prompt.trim().toLowerCase();
  const match = REPORT_SELECTOR_TEST_EXAMPLES.find(
    (example) =>
      example.prompt.trim().toLowerCase() === normalized ||
      (example.aliases?.some(
        (alias) => alias.trim().toLowerCase() === normalized
      ) ??
        false)
  );
  return match?.reportType ?? null;
};

const buildReportSelectorTestExampleLines = (): string =>
  REPORT_SELECTOR_TEST_EXAMPLES.flatMap((example) => [
    `- User: "${example.prompt}"`,
    `  → { "reportType": "${example.reportType}", "confidence": 95, "reason": "${example.reason}" }`,
  ]).join('\n');

export const buildReportSelectorSystemPrompt = (): string => {
  const enabledTypes = getEnabledReportTypes();
  const reportTypeUnion = enabledTypes.map((type) => `"${type}"`).join(' | ');

  return `You select the best legacy Revelio report for a user request.
Return JSON only:
{
  "reportType": ${reportTypeUnion} | null,
  "confidence": 0-100,
  "reason": "short explanation or null"
}

Rules:
- Pick exactly one report type when the user request clearly fits; otherwise null.
- confidence reflects match strength (0 = no match, 100 = perfect).
- Prefer null over weak matches.
- Labor market conditions, hiring difficulty, and market tightness questions fit Market Conditions even when the user could also view the topic as a dashboard chart.
- Company prestige scores, employer reputation rankings, and prestige benchmarks for a named company fit Prestige (not Sentiment).

Report selection hints:
${buildReportSelectorHintLines()}

Available reports:
${buildReportSelectorCatalogLines()}

Deterministic test examples (match closely when the user prompt is one of these):
${buildReportSelectorTestExampleLines()}`;
};

export const GLOBAL_ASSISTANT_REPORT_PARAMETER_MAPPINGS: readonly {
  reportType: string;
  parameters: readonly GlobalAssistantReportParameterMapping[];
}[] = getEnabledCatalogEntries().map((entry) => ({
  reportType: entry.reportType,
  parameters: entry.parameters.map((parameter) => ({
    parameterId: parameter.parameterId,
    displayName: parameter.displayName,
    mwColumnLabels: parameter.mwColumnLabels,
    promotedParameterIds: parameter.promotedParameterIds,
  })),
}));

export const getReportParameterMapping = (
  reportType: string,
  parameterId: string
): GlobalAssistantReportParameterMapping | undefined =>
  GLOBAL_ASSISTANT_REPORT_PARAMETER_MAPPINGS.find(
    (entry) => entry.reportType === reportType
  )?.parameters.find((parameter) => parameter.parameterId === parameterId);

export const getMwColumnLabelForParameter = (
  reportType: string,
  parameterId: string
): string | undefined => {
  const mapping = getReportParameterMapping(reportType, parameterId);
  return mapping?.mwColumnLabels[0];
};

export const getProjectionRules = (reportType: ReportTypeEnum) =>
  getCatalogEntry(reportType)?.projectionRules ?? [];
